import DOMPurify from 'dompurify';
import React, {useEffect, useState, useCallback} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import './styles.scss';

interface AuthorizedImageProps {
  source: string;
  className?: any;
  height?: any;
  width?: any;
  alt?: string;
  style?: any;
  onClick?: any;
  onError?: any;
  accessToken: any;
  default?: any;
  requiresAuthorization?: boolean;
}

function AuthorizedImage({
  source,
  className,
  accessToken,
  requiresAuthorization = true,
  ...props
}: AuthorizedImageProps) {
  const [imageSource, setImageSource] = useState<string>('');
  const [svgText, setSvgText] = useState<string>('');
  const [fetchAgain, setFetchAgain] = useState<number>(0);

  const fetchImage = useCallback(async () => {
    if (
      source.includes('BOnull') ||
      source.includes('BOundefined') ||
      source.includes('object%20Object')
    ) {
      return;
    }
    const access_token = localStorage.getItem('access_token') || accessToken;
    if (access_token && fetchAgain < 2) {
      try {
        const response = await fetch(source, {
          headers: requiresAuthorization
            ? {
                Authorization: 'Bearer ' + access_token,
                X_MYADV_API_KEY:
                  process.env.REACT_APP_DEFAULT_TENANT_ALIAS || '',
              }
            : {
                X_MYADV_API_KEY:
                  process.env.REACT_APP_DEFAULT_TENANT_ALIAS || '',
              },
        });

        if (response.status === 404 || response.status === 401) {
          setFetchAgain(fetchAgain + 1);
        } else {
          if (source.includes('.svg')) {
            setSvgText(DOMPurify.sanitize(await response.text()));
          } else {
            await response.blob().then((blobResponse) => {
              const urlCreator = window.URL || window.webkitURL;
              setImageSource(urlCreator.createObjectURL(blobResponse));
            });
          }
        }
      } catch (error) {
        console.log('Error fetching image:', error);
      }
    } else {
      setImageSource(props?.default);
    }
  }, [source, fetchAgain]);

  const fetchImageNoAuth = useCallback(async () => {
    if (
      source.includes('BOnull') ||
      source.includes('BOundefined') ||
      source.includes('object%20Object')
    ) {
      return;
    }
    if (fetchAgain < 2) {
      try {
        const response = await fetch(source, {
          headers: {
            X_MYADV_API_KEY: process.env.REACT_APP_DEFAULT_TENANT_ALIAS || '',
          },
        });

        if (response.status === 404 || response.status === 401) {
          setFetchAgain(fetchAgain + 1);
        } else {
          if (source.includes('.svg')) {
            setSvgText(DOMPurify.sanitize(await response.text()));
          } else {
            await response.blob().then((blobResponse) => {
              const urlCreator = window.URL || window.webkitURL;
              setImageSource(urlCreator.createObjectURL(blobResponse));
            });
          }
        }
      } catch (error) {
        console.log('Error fetching image:', error);
      }
    } else {
      setImageSource(props?.default);
    }
  }, [source, fetchAgain]);

  useEffect(() => {
    if (localStorage.getItem('access_token') || accessToken) {
      fetchImage();
    } else {
      fetchImageNoAuth();
    }
  }, [fetchImage, fetchImageNoAuth]);

  return (
    <>
      {imageSource && (
        <img
          src={imageSource}
          style={props.style}
          className={className ? className : 'app-spinner-default'}
          onError={(err) => {
            console.log('Error loading image:', err.type);
            setFetchAgain(fetchAgain + 1);
          }}
          {...props}
        />
      )}
      {svgText && (
        <div
          key={svgText}
          className={className}
          style={props.style}
          dangerouslySetInnerHTML={{__html: svgText}}
          {...props}
        />
      )}
    </>
  );
}

const mapStateToProps = ({accessToken}: any) => ({
  accessToken: accessToken.value,
});

export default compose(connect(mapStateToProps))(React.memo(AuthorizedImage));
